import moment from 'moment';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
// import { createImageVariantConfig } from '../util/sdkLoader';
import { v4 as uuidv4 } from 'uuid';
import { createImageVariantConfig } from '../util/sdkLoader';
import { getStartOf, monthIdString, isAfterDate } from '../util/dates';
import { getMonthsForDateRange, getMonthsToFetch } from '../util/dateRangeHelpers';
import { getBookingPackageType } from '../util/listingAvailabilityHelpers';
import { getDefaultCategory } from '../util/booking';
import { computeListingBlockedDates, unionBlockedDates, enumerateDateStrings } from '../util/cartAvailability';
import {
  getCartFromSession,
  addItemToSessionStorageCart,
  removeItemFromSessionStorageCart,
  saveCartToSession,
  markCartAuthorAddedThisSession,
} from '../util/sessionStorageManager';
// const { types } = require('sharetribe-flex-sdk');
// const { Money } = types;

// RAS-2508: serializes every cart write (add/update/remove/clear) so a later-fired
// write can never be clobbered by an earlier one that happens to resolve after it.
// Each write function passed in re-reads the cart itself (fresh sdk.currentUser.show()
// or sessionStorage) only once its turn arrives, rather than trusting a snapshot taken
// before it was queued - so a write queued behind an in-flight one still layers onto
// that write's result instead of overwriting it. This only serializes writes that
// originate in this tab; it can't by itself guarantee ordering against a genuinely
// simultaneous write from another browser tab, since the Flex SDK's updateProfile has
// no optimistic-concurrency/version check to detect that case - reading fresh
// immediately before writing narrows that window as much as is achievable client-side.
let cartWriteQueue = Promise.resolve();

export const enqueueCartWrite = writeFn => {
  const result = cartWriteQueue.then(writeFn, writeFn);
  // Swallow rejections here only to keep the queue itself moving - `result` (returned
  // below) still carries the real rejection back to this call's own caller.
  cartWriteQueue = result.catch(() => {});
  return result;
};

// RAS-2520: a listing that sells more than one booking category (the booking panel's
// Daily/Hourly/Minute toggle) can hold two genuinely different cart entries whose start
// and end instants coincide - e.g. a 1-day daily booking 00:00->00:00 and a 24-hour
// hourly one. Matching on (listingId, dates) alone treated the second add as a re-add of
// the first, which merges: it sums the quantity AND overwrites the stored `package`,
// silently converting the existing booking into the other category. Comparing package
// TYPE (daily vs hourly) rather than the exact tier keeps the intended merge behaviour
// for same-category re-adds, where the tier is derived from the duration anyway.
//
// packageKey is optional: callers that don't pass one (or items predating the stored
// `package` field) fall back to the original dates-only comparison unchanged.
const isSameCartBooking = (item, listingId, startDate, endDate, packageKey) => {
  if (item.listingId !== listingId) return false;
  if (!moment(item.bookingDates.startDate).isSame(startDate)) return false;
  if (!moment(item.bookingDates.endDate).isSame(endDate)) return false;
  if (!packageKey || !item.package) return true;

  // RAS-2520: compare full booking CATEGORY, which separates overnight from daily -
  // a 1-night overnight booking and a 1-day daily booking cover identical instants but
  // are charged at different rates (`night` vs `day` unitType server-side), so merging
  // them would silently reprice one of the two. getBookingPackageType's coarser
  // daily/hourly answer can't see that difference; it stays the fallback for legacy
  // entries whose package key isn't recognised.
  const itemCategory = getDefaultCategory(item.package);
  const addedCategory = getDefaultCategory(packageKey);
  if (itemCategory && addedCategory) {
    return itemCategory === addedCategory;
  }
  return (
    getBookingPackageType(item.package, item.bookingDates.startDate, item.bookingDates.endDate) ===
    getBookingPackageType(packageKey, startDate, endDate)
  );
};

export const existingCartItemIndex = (cart, listingId, startDate, endDate, packageKey) => {
  return cart.findIndex(item => isSameCartBooking(item, listingId, startDate, endDate, packageKey));
};

export const getExistingCartItem = (cart = [], listingId, startDate, endDate, packageKey) => {
  return cart.find(item => isSameCartBooking(item, listingId, startDate, endDate, packageKey));
};

// Helper function to generate item operation key for per-item tracking
export const getItemOperationKey = (listingId, bookingDates, cartItemId = null) => {
  if (cartItemId) {
    return `cart-item-${cartItemId}`;
  }
  if (!bookingDates?.startDate || !bookingDates?.endDate) {
    return `listing-${listingId}`;
  }
  const startDate = moment(bookingDates.startDate).format('YYYY-MM-DD');
  const endDate = moment(bookingDates.endDate).format('YYYY-MM-DD');
  return `${listingId}:${startDate}:${endDate}`;
};

// Helper function to get user-friendly error messages
export const getUserFriendlyCartErrorMessage = (error, intl) => {
  if (!error) {
    return intl?.formatMessage({
      id: 'Cart.error.generic',
      defaultMessage: 'Something went wrong. Please try again.'
    }) || 'Something went wrong. Please try again.';
  }

  // Check for quantity/stock errors (already user-friendly)
  if (error.message?.includes('Maximum quantity available')) {
    return error.message;
  }

  if (error.message?.includes('Required data is missing')) {
    return intl?.formatMessage({
      id: 'Cart.error.missingData',
      defaultMessage: 'Some required information is missing. Please refresh the page and try again.'
    }) || 'Some required information is missing. Please refresh the page and try again.';
  }

  if (error.message?.includes('User not found')) {
    return intl?.formatMessage({
      id: 'Cart.error.userNotFound',
      defaultMessage: 'Your session may have expired. Please refresh the page and try again.'
    }) || 'Your session may have expired. Please refresh the page and try again.';
  }

  // Check for stock/availability errors from API
  const apiErrors = error.apiErrors || [];
  const stockError = apiErrors.find(e =>
    e.code === 'TRANSACTION_LISTING_INSUFFICIENT_STOCK' ||
    e.code === 'TRANSACTION_BOOKING_TIME_NOT_AVAILABLE'
  );

  if (stockError) {
    return intl?.formatMessage({
      id: 'Cart.error.unavailable',
      defaultMessage: 'This item is no longer available for the selected dates. Please select different dates.'
    }) || 'This item is no longer available for the selected dates. Please select different dates.';
  }

  // Use generic error message if available
  if (error.message) {
    return error.message;
  }

  // Fallback
  return intl?.formatMessage({
    id: 'Cart.error.generic',
    defaultMessage: 'Something went wrong. Please try again.'
  }) || 'Something went wrong. Please try again.';
};

// Sort itemsByAuthor object so authors with most recent items come first
// Returns a new object with keys in sorted order (JS objects maintain insertion order)
const sortItemsByAuthorRecency = (itemsByAuthor) => {
  const sortedKeys = Object.keys(itemsByAuthor).sort((a, b) => {
    const aLatest = itemsByAuthor[a]?.[0]?.addedAt;
    const bLatest = itemsByAuthor[b]?.[0]?.addedAt;
    const aTime = aLatest ? new Date(aLatest).getTime() : 0;
    const bTime = bLatest ? new Date(bLatest).getTime() : 0;
    return bTime - aTime;
  });

  return sortedKeys.reduce((acc, key) => {
    acc[key] = itemsByAuthor[key];
    return acc;
  }, {});
};

export const getItemsByAuthor = (cart = [], sdk) => {
  const queriedListingIds = cart.map(c => c.listingId);

  if (queriedListingIds.length === 0) {
    return Promise.resolve({ itemsByAuthor: {}, totalItems: 0 })
  };


  const variantPrefix = 'listing-card';
  const aspectRatio = 1

  return sdk.listings.query({
    ids: queriedListingIds,
    include: ['author', 'author.profileImage', 'images'],
    // Cart only renders small thumbnails — fetch minimal variants to reduce payload
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  }).then(response => {
    const denormalizedListings = denormalisedResponseEntities(response);
    const listingMap = new Map(denormalizedListings.map(listing => [listing.id.uuid, listing]));

    const updatedCart = cart.map(cartItem => {
      const listing = listingMap.get(cartItem.listingId);
      return {
        ...cartItem,
        listing,
      }
    })

    const itemsByAuthor = updatedCart.reduce((acc, item) => {
      const { authorId } = item;
      acc[authorId] = acc[authorId] || [];
      acc[authorId].push(item);
      return acc;
    }, {});

    // Sort items by addedAt timestamp (latest first), with backward compatibility for items without timestamp
    Object.keys(itemsByAuthor).forEach(authorId => {
      itemsByAuthor[authorId].sort((a, b) => {
        const aTime = a.addedAt ? new Date(a.addedAt).getTime() : 0;
        const bTime = b.addedAt ? new Date(b.addedAt).getTime() : 0;
        return bTime - aTime; // Descending order (newest first)
      });
    });

    // Sort authors by most recent item
    const sortedItemsByAuthor = sortItemsByAuthorRecency(itemsByAuthor);

    return { itemsByAuthor: sortedItemsByAuthor, totalItems: updatedCart.length };
  })
}

/**
 * RAS-2501: groups cart items across ALL authors into one row per
 * (authorId, exact booking date range) - not one row per author alone. The same
 * store can have items booked across genuinely different rental windows, and the
 * "view all cart items" panel shows those as separate rows (confirmed in Figma:
 * e.g. "Cody Party Rentals Nepean" Sep 10-16 and Oct 10-16 are two distinct rows).
 *
 * Items missing authorId or bookingDates are skipped (logged as a warning, not
 * thrown) rather than crashing the whole panel or silently grouping them into an
 * "undefined" bucket - authorId has been a required field on every cart item since
 * addToCart's own validation (cart.duck.js's addToCart throws "Required data is
 * missing" if it's absent when an item is added), so a missing value here would
 * indicate corrupted/legacy stored data, not a normal runtime case.
 */
/**
 * The stable key of one cart group: (authorId, calendar start day, calendar end day).
 * Exported so a caller that is about to MOVE a group to new dates (the cart page's
 * inline date editor) can name the key the items will land under before the write
 * resolves, using this exact rule rather than a re-typed copy of it.
 */
export const buildCartGroupKey = (authorId, startDate, endDate) =>
  `${authorId}::${moment(startDate).format('YYYY-MM-DD')}::${moment(endDate).format(
    'YYYY-MM-DD'
  )}`;

export const groupCartItemsByAuthorAndDateRange = (itemsByAuthor = {}) => {
  const groups = [];

  // state.cart.items is null (not undefined) until fetchCartItems has answered - a
  // default parameter doesn't cover that, and Object.keys(null) throws. Every page
  // that renders on a hard reload goes through this before the cart arrives.
  Object.keys(itemsByAuthor || {}).forEach(authorId => {
    const items = itemsByAuthor[authorId] || [];
    const groupsByKey = {};

    items.forEach(item => {
      if (!item.authorId) {
        console.warn(
          'groupCartItemsByAuthorAndDateRange: skipping cart item with missing authorId',
          item?.id
        );
        return;
      }
      const { startDate, endDate } = item.bookingDates || {};
      if (!startDate || !endDate) {
        console.warn(
          'groupCartItemsByAuthorAndDateRange: skipping cart item with missing bookingDates',
          item?.id
        );
        return;
      }

      const key = buildCartGroupKey(item.authorId, startDate, endDate);
      if (!groupsByKey[key]) {
        // `key` is exposed so consumers that need a stable map key per group (the
        // cart page's per-cart line-item state, active-cart tracking) use this exact
        // one rather than re-deriving it.
        groupsByKey[key] = { key, authorId: item.authorId, startDate, endDate, items: [] };
      }
      groupsByKey[key].items.push(item);
    });

    groups.push(...Object.values(groupsByKey));
  });

  // Each group's items[0] is already its most-recently-added item (itemsByAuthor's
  // own arrays are pre-sorted newest-first by getItemsByAuthor above) - reuse that
  // to sort groups by recency too, same convention as sortItemsByAuthorRecency.
  groups.sort((a, b) => {
    const aTime = a.items[0]?.addedAt ? new Date(a.items[0].addedAt).getTime() : 0;
    const bTime = b.items[0]?.addedAt ? new Date(b.items[0].addedAt).getTime() : 0;
    return bTime - aTime;
  });

  return groups;
};

// Action Types
export const SET_ITEMS_REQUEST = 'app/cart/SET_ITEMS_REQUEST';
export const SET_ITEMS_SUCCESS = 'app/cart/SET_ITEMS_SUCCESS';
export const SET_ITEMS_ERROR = 'app/cart/SET_ITEMS_ERROR';

export const ADD_TO_CART_REQUEST = 'app/cart/ADD_TO_CART';
export const ADD_TO_CART_SUCCESS = 'app/cart/ADD_TO_CART_SUCCESS';
export const ADD_TO_CART_ERROR = 'app/cart/ADD_TO_CART_ERROR';

export const CLEAR_NOTIFICATION = 'app/cart/CLEAR_NOTIFICATION';

export const UPDATE_CART_REQUEST = 'app/cart/UPDATE_CART_REQUEST';
export const UPDATE_CART_SUCCESS = 'app/cart/UPDATE_CART_SUCCESS';
export const UPDATE_CART_ERROR = 'app/cart/UPDATE_CART_ERROR';

export const REMOVE_CART_ITEM_REQUEST = 'app/cart/REMOVE_CART_ITEM_REQUEST';
export const REMOVE_CART_ITEM_SUCCESS = 'app/cart/REMOVE_CART_ITEM_SUCCESS';
export const REMOVE_CART_ITEM_ERROR = 'app/cart/REMOVE_CART_ITEM_ERROR';

export const CLEAR_CART_ITEM_REQUEST = 'app/cart/CLEAR_CART_ITEM_REQUEST';
export const CLEAR_CART_ITEM_SUCCESS = 'app/cart/CLEAR_CART_ITEM_SUCCESS';
export const CLEAR_CART_ITEM_ERROR = 'app/cart/CLEAR_CART_ITEM_ERROR';

// Per-item operation action types
export const ADD_TO_CART_ITEM_REQUEST = 'app/cart/ADD_TO_CART_ITEM_REQUEST';
export const ADD_TO_CART_ITEM_SUCCESS = 'app/cart/ADD_TO_CART_ITEM_SUCCESS';
export const ADD_TO_CART_ITEM_ERROR = 'app/cart/ADD_TO_CART_ITEM_ERROR';

export const UPDATE_CART_ITEM_REQUEST = 'app/cart/UPDATE_CART_ITEM_REQUEST';
export const UPDATE_CART_ITEM_SUCCESS = 'app/cart/UPDATE_CART_ITEM_SUCCESS';
export const UPDATE_CART_ITEM_ERROR = 'app/cart/UPDATE_CART_ITEM_ERROR';

// Notification management
export const SHOW_NOTIFICATION = 'app/cart/SHOW_NOTIFICATION';
export const CLEAR_ITEM_ERROR = 'app/cart/CLEAR_ITEM_ERROR';

// RAS-2509: cart-drawer date-picker availability action types
export const FETCH_CART_TIME_SLOTS_REQUEST = 'app/cart/FETCH_CART_TIME_SLOTS_REQUEST';
export const FETCH_CART_TIME_SLOTS_SUCCESS = 'app/cart/FETCH_CART_TIME_SLOTS_SUCCESS';
export const FETCH_CART_TIME_SLOTS_ERROR = 'app/cart/FETCH_CART_TIME_SLOTS_ERROR';

// Reducer

const initialState = {
  items: null,
  itemsCount: 0,
  setItemsInProgress: false,
  setItemsError: null,
  addToCartInProgress: false,
  addToCartError: null,
  notification: null, // Updated to support object: { message, type, itemKey }
  updateCartInProgress: false,
  updateCartError: null,
  removeFromCartInProgress: false,
  removeFromCartError: null,
  removableItemId: null,
  clearCartItemInProgress: false,
  clearCartItemError: null,
  // NEW: Per-item operation tracking
  itemOperationsInProgress: {}, // Key: itemKey, Value: { operation: 'add'|'update', listingId, cartItemId? }
  itemOperationErrors: {}, // Key: itemKey, Value: { message, type: 'add'|'update', error }
  // RAS-2509: { [listingId]: { [monthId]: { timeSlots, fetchTimeSlotsInProgress, fetchTimeSlotsError } } }
  // Lives here (not a page duck) because the cart drawer opens from multiple pages.
  monthlyTimeSlotsByListing: {},
};

export default function reducer(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case SET_ITEMS_REQUEST:
      return { ...state, setItemsInProgress: true, setItemsError: null };
    case SET_ITEMS_SUCCESS:
      return {
        ...state,
        setItemsInProgress: false,
        items: payload.items,
        itemsCount: payload.itemsCount,
      };
    case SET_ITEMS_ERROR:
      return { ...state, setItemsInProgress: false, setItemsError: payload };
    case ADD_TO_CART_REQUEST:
      return { ...state, addToCartError: null, addToCartInProgress: true };
    case ADD_TO_CART_SUCCESS:
      return {
        ...state,
        addToCartInProgress: false,
        notification: typeof payload.notification === 'object' ? payload.notification : { message: 'Rental added to cart', type: 'success', operation: 'add' },
        items: payload.items,
        itemsCount: payload.itemsCount,
      };
    case ADD_TO_CART_ERROR:
      return {
        ...state,
        addToCartInProgress: false,
        addToCartError: payload,
        notification: typeof payload.notification === 'object' ? payload.notification : { message: payload.message || 'Failed to add item to cart!', type: 'error', operation: 'add' },
      };
    case CLEAR_NOTIFICATION:
      return { ...state, notification: null };
    // Per-item add operations
    case ADD_TO_CART_ITEM_REQUEST:
      return {
        ...state,
        itemOperationsInProgress: {
          ...state.itemOperationsInProgress,
          [payload.itemKey]: {
            operation: 'add',
            listingId: payload.listingId
          }
        },
        itemOperationErrors: {
          ...state.itemOperationErrors,
          [payload.itemKey]: undefined // Clear previous error
        }
      };
    case ADD_TO_CART_ITEM_SUCCESS:
      const { [payload.itemKey]: removedAdd, ...remainingAddInProgress } = state.itemOperationsInProgress;
      return {
        ...state,
        itemOperationsInProgress: remainingAddInProgress,
        items: payload.items,
        itemsCount: payload.itemsCount,
        notification: {
          message: 'Item added to cart successfully',
          type: 'success',
          itemKey: payload.itemKey,
          operation: 'add'
        }
      };
    case ADD_TO_CART_ITEM_ERROR:
      const { [payload.itemKey]: removedAddError, ...remainingAddErrors } = state.itemOperationsInProgress;
      return {
        ...state,
        itemOperationsInProgress: remainingAddErrors,
        itemOperationErrors: {
          ...state.itemOperationErrors,
          [payload.itemKey]: {
            message: payload.error.message || 'Failed to add item to cart',
            type: 'add',
            error: payload.error
          }
        },
        notification: {
          message: payload.error.message || 'Failed to add item to cart',
          type: 'error',
          itemKey: payload.itemKey,
          operation: 'add'
        }
      };
    // Per-item update operations
    case UPDATE_CART_ITEM_REQUEST:
      return {
        ...state,
        itemOperationsInProgress: {
          ...state.itemOperationsInProgress,
          [payload.itemKey]: {
            operation: 'update',
            listingId: payload.listingId,
            cartItemId: payload.cartItemId
          }
        },
        itemOperationErrors: {
          ...state.itemOperationErrors,
          [payload.itemKey]: undefined // Clear previous error
        }
      };
    case UPDATE_CART_ITEM_SUCCESS:
      const { [payload.itemKey]: removedUpdate, ...remainingUpdateInProgress } = state.itemOperationsInProgress;
      return {
        ...state,
        itemOperationsInProgress: remainingUpdateInProgress,
        items: payload.items,
        itemsCount: payload.itemsCount,
        notification: {
          message: 'Cart updated successfully',
          type: 'success',
          itemKey: payload.itemKey,
          operation: 'update'
        }
      };
    case UPDATE_CART_ITEM_ERROR:
      const { [payload.itemKey]: removedUpdateError, ...remainingUpdateErrors } = state.itemOperationsInProgress;
      return {
        ...state,
        itemOperationsInProgress: remainingUpdateErrors,
        itemOperationErrors: {
          ...state.itemOperationErrors,
          [payload.itemKey]: {
            message: payload.error.message || 'Failed to update cart item',
            type: 'update',
            error: payload.error
          }
        },
        notification: {
          message: payload.error.message || 'Failed to update cart item',
          type: 'error',
          itemKey: payload.itemKey,
          operation: 'update'
        }
      };
    case SHOW_NOTIFICATION:
      return {
        ...state,
        notification: payload
      };
    case CLEAR_ITEM_ERROR:
      const { [payload.itemKey]: removedItemError, ...remainingItemErrors } = state.itemOperationErrors;
      return {
        ...state,
        itemOperationErrors: remainingItemErrors
      };
    case UPDATE_CART_REQUEST:
      return { ...state, updateCartError: null, updateCartInProgress: true };
    case UPDATE_CART_SUCCESS:
      return {
        ...state,
        updateCartInProgress: false,
        items: payload.items,
        itemsCount: payload.itemsCount,
      };
    case UPDATE_CART_ERROR:
      return {
        ...state,
        updateCartInProgress: false,
        updateCartError: payload,
      };
    case REMOVE_CART_ITEM_REQUEST:
      return {
        ...state,
        removeFromCartInProgress: true,
        removeFromCartError: null,
        removableItemId: payload,
      };
    case REMOVE_CART_ITEM_SUCCESS:
      return {
        ...state,
        removeFromCartInProgress: false,
        items: payload.items,
        itemsCount: payload.itemsCount,
      };
    case REMOVE_CART_ITEM_ERROR:
      return {
        ...state,
        removeFromCartInProgress: false,
        removeFromCartError: payload,
      };
    case CLEAR_CART_ITEM_REQUEST:
      return {
        ...state,
        clearCartItemInProgress: true,
        clearCartItemError: null,
      };
    case CLEAR_CART_ITEM_SUCCESS:
      return {
        ...state,
        clearCartItemInProgress: false,
        items: payload.items,
        itemsCount: payload.itemsCount,
      };
    case CLEAR_CART_ITEM_ERROR:
      return {
        ...state,
        clearCartItemInProgress: false,
        clearCartItemError: payload,
      };
    case FETCH_CART_TIME_SLOTS_REQUEST: {
      const { listingId, monthId } = payload;
      const listingSlots = state.monthlyTimeSlotsByListing[listingId] || {};
      return {
        ...state,
        monthlyTimeSlotsByListing: {
          ...state.monthlyTimeSlotsByListing,
          [listingId]: {
            ...listingSlots,
            [monthId]: {
              ...listingSlots[monthId],
              fetchTimeSlotsInProgress: true,
              fetchTimeSlotsError: null,
            },
          },
        },
      };
    }
    case FETCH_CART_TIME_SLOTS_SUCCESS: {
      const { listingId, monthId, timeSlots } = payload;
      const listingSlots = state.monthlyTimeSlotsByListing[listingId] || {};
      return {
        ...state,
        monthlyTimeSlotsByListing: {
          ...state.monthlyTimeSlotsByListing,
          [listingId]: {
            ...listingSlots,
            [monthId]: {
              ...listingSlots[monthId],
              fetchTimeSlotsInProgress: false,
              timeSlots,
            },
          },
        },
      };
    }
    case FETCH_CART_TIME_SLOTS_ERROR: {
      const { listingId, monthId, error } = payload;
      const listingSlots = state.monthlyTimeSlotsByListing[listingId] || {};
      return {
        ...state,
        monthlyTimeSlotsByListing: {
          ...state.monthlyTimeSlotsByListing,
          [listingId]: {
            ...listingSlots,
            [monthId]: {
              ...listingSlots[monthId],
              fetchTimeSlotsInProgress: false,
              fetchTimeSlotsError: error,
            },
          },
        },
      };
    }
    default:
      return state;
  }
}

// Action Creators
const setItemsRequest = () => ({ type: SET_ITEMS_REQUEST });
export const setItemsSuccess = (items, itemsCount) => ({
  type: SET_ITEMS_SUCCESS,
  payload: { items, itemsCount },
});
const setItemsError = err => ({ type: SET_ITEMS_ERROR, payload: err });

const addToCartRequest = () => ({ type: ADD_TO_CART_REQUEST });
const addToCartSuccess = (items, itemsCount, additionalData) => ({
  type: ADD_TO_CART_SUCCESS,
  payload: { items, itemsCount, additionalData },
});
const addToCartError = err => ({ type: ADD_TO_CART_ERROR, payload: err });

const removeFromCartRequest = itemId => ({
  type: REMOVE_CART_ITEM_REQUEST,
  payload: itemId,
});
const removeFromCartSuccess = (items, itemsCount) => ({
  type: REMOVE_CART_ITEM_SUCCESS,
  payload: { items, itemsCount },
});
const removeFromCartError = err => ({
  type: REMOVE_CART_ITEM_ERROR,
  payload: err,
});

export const clearNotification = () => ({ type: CLEAR_NOTIFICATION });

// Per-item action creators
export const addToCartItemRequest = (itemKey, listingId) => ({
  type: ADD_TO_CART_ITEM_REQUEST,
  payload: { itemKey, listingId, operation: 'add' }
});

export const addToCartItemSuccess = (itemKey, items, itemsCount) => ({
  type: ADD_TO_CART_ITEM_SUCCESS,
  payload: { itemKey, items, itemsCount }
});

export const addToCartItemError = (itemKey, error) => ({
  type: ADD_TO_CART_ITEM_ERROR,
  payload: { itemKey, error, operation: 'add' }
});

export const updateCartItemRequest = (itemKey, listingId, cartItemId) => ({
  type: UPDATE_CART_ITEM_REQUEST,
  payload: { itemKey, listingId, cartItemId, operation: 'update' }
});

export const updateCartItemSuccess = (itemKey, items, itemsCount) => ({
  type: UPDATE_CART_ITEM_SUCCESS,
  payload: { itemKey, items, itemsCount }
});

export const updateCartItemError = (itemKey, error) => ({
  type: UPDATE_CART_ITEM_ERROR,
  payload: { itemKey, error, operation: 'update' }
});

export const showNotification = (message, type = 'info', itemKey = null) => ({
  type: SHOW_NOTIFICATION,
  payload: { message, type, itemKey }
});

export const clearItemError = (itemKey) => ({
  type: CLEAR_ITEM_ERROR,
  payload: { itemKey }
});

export const updateCartRequest = () => ({
  type: UPDATE_CART_REQUEST,
});

export const updateCartSuccess = (items, itemsCount) => {
  return {
    type: UPDATE_CART_SUCCESS,
    payload: { items, itemsCount },
  };
};

export const updateCartError = error => ({
  type: UPDATE_CART_ERROR,
  error,
});

export const clearCartItemRequest = () => ({ type: CLEAR_CART_ITEM_REQUEST });
export const clearCartItemSuccess = (items, itemsCount) => ({
  type: CLEAR_CART_ITEM_SUCCESS,
  payload: { items, itemsCount },
});
export const clearCartItemError = err => ({
  type: CLEAR_CART_ITEM_ERROR,
  payload: err,
});

export const fetchCartTimeSlotsRequest = (listingId, monthId) => ({
  type: FETCH_CART_TIME_SLOTS_REQUEST,
  payload: { listingId, monthId },
});
export const fetchCartTimeSlotsSuccess = (listingId, monthId, timeSlots) => ({
  type: FETCH_CART_TIME_SLOTS_SUCCESS,
  payload: { listingId, monthId, timeSlots },
});
export const fetchCartTimeSlotsError = (listingId, monthId, error) => ({
  type: FETCH_CART_TIME_SLOTS_ERROR,
  error: true,
  payload: { listingId, monthId, error },
});

// Thunks
export const fetchCartItems = () => async (dispatch, getState, sdk) => {
  dispatch(setItemsRequest());
  const { isAuthenticated } = getState().auth;
  let cart = [];

  if (!isAuthenticated) {
    cart = getCartFromSession() || [];
  } else {
    const userRes = await sdk.currentUser.show();
    const currentUser = userRes.data.data;
    cart = currentUser?.attributes?.profile?.publicData?.cart || [];
  }

  const { itemsByAuthor, totalItems } = await getItemsByAuthor(cart, sdk);
  dispatch(setItemsSuccess(itemsByAuthor, totalItems));
  return { itemsByAuthor, totalItems };
};


export const addToCart = (config, params, queryID) => async (dispatch, getState, sdk) => {
  const {
    selectedPackage,
    bookingDates = {},
    timeZone,
    listingId,
    authorId,
    quantity = 1,
    units,
    usageMultipleValues,
    selectedFeeTypes,
    pickupAtOwnerLocation,
    returnAtOwnerLocation,
  } = params;

  const { startDate, endDate } = bookingDates;

  // Generate item key for per-item tracking
  const itemKey = getItemOperationKey(listingId, bookingDates);

  // Dispatch per-item request
  dispatch(addToCartItemRequest(itemKey, listingId));
  // Keep global request for backward compatibility
  dispatch(addToCartRequest());

  const { isAuthenticated } = getState().auth;

  try {
    // Fetch listing to check available stock/quantity (for both guest and authenticated users)
    const listingRes = await sdk.listings.show({ id: listingId });
    const listing = listingRes?.data?.data;
    const maxAvailableSeats = listing?.attributes?.publicData?.quantity;

    let userTokenMaybe = {};

    // RAS-2508: the actual read-compute-write goes through the shared write queue so
    // it always starts from the freshest cart available at the moment it actually runs,
    // not a snapshot taken before other queued writes had a chance to land.
    const updatedCart = await enqueueCartWrite(async () => {
      if (!isAuthenticated) {
        // Guest user: store cart in local/session storage
        const cart = getCartFromSession() || [];

        // Calculate total quantity for this listing in cart (across all matching items)
        const existingItemsForListing = getExistingCartItem(cart, listingId, startDate, endDate, selectedPackage);
        const existingQuantityForListing = existingItemsForListing?.quantity || 0;
        const totalQuantityAfterAdd = existingQuantityForListing + Number(quantity);

        // Validate against max available seats/quantity
        if (maxAvailableSeats != null && totalQuantityAfterAdd > maxAvailableSeats) {
          throw new Error(`Maximum quantity available is ${maxAvailableSeats}. You already have ${existingQuantityForListing} in your cart.`);
        }

        return addItemToSessionStorageCart({ ...params, queryID });
      }

      // Authenticated user
      const userRes = await sdk.currentUser.show();
      const currentUser = userRes.data?.data;

      if (!currentUser?.id?.uuid) throw new Error('User not found');

      userTokenMaybe = { userToken: currentUser.id.uuid };

      // Validation
      if (!selectedPackage || !startDate || !endDate || !listingId || !authorId || !timeZone || !units) {
        throw new Error('Required data is missing');
      }

      const cart = currentUser?.attributes?.profile?.publicData?.cart || [];

      // Try to find existing cart item
      const itemIndex = existingCartItemIndex(cart, listingId, startDate, endDate, selectedPackage);

      // Calculate total quantity for this listing in cart (across all matching items)
      const existingItemsForListing = getExistingCartItem(cart, listingId, startDate, endDate, selectedPackage);
      const existingQuantityForListing = existingItemsForListing?.quantity || 0;

      const totalQuantityAfterAdd = existingQuantityForListing + Number(quantity);

      // Validate against max available seats/quantity
      if (maxAvailableSeats != null && totalQuantityAfterAdd > maxAvailableSeats) {
        throw new Error(`Maximum quantity available is ${maxAvailableSeats}. You already have ${existingQuantityForListing} in your cart.`);
      }

      let nextCart;
      if (itemIndex !== -1) {
        // Update existing item: quantity and optional fields
        nextCart = [...cart];
        nextCart[itemIndex] = {
          ...nextCart[itemIndex],
          package: selectedPackage,
          quantity: totalQuantityAfterAdd,
          ...(queryID && { queryID }),
          usageMultipleValues,
          selectedFeeTypes,
          pickupAtOwnerLocation,
          returnAtOwnerLocation,
        };
      } else {
        // Add new item
        nextCart = [
          ...cart,
          {
            id: uuidv4(),
            authorId,
            listingId,
            package: selectedPackage,
            bookingDates,
            quantity: Number(quantity),
            timeZone,
            units: Number(units),
            ...(queryID && { queryID }),
            usageMultipleValues,
            selectedFeeTypes,
            pickupAtOwnerLocation,
            returnAtOwnerLocation,
            addedAt: new Date().toISOString(), // Timestamp for sorting (latest first)
          },
        ];
      }

      // Save updated cart to user's public profile
      await sdk.currentUser.updateProfile(
        {
          publicData: {
            cart: nextCart,
          },
        },
        { expand: true }
      );

      return nextCart;
    });

    // The write landed: this store's cart was added to in this browser session (feeds
    // the different-owner popup's gate, shouldPromptForDifferentOwner). Recorded here,
    // after the write, so a refused add (stock, validation) never counts.
    markCartAuthorAddedThisSession(authorId);

    // Update Redux store cart state
    const { itemsByAuthor, totalItems } = await getItemsByAuthor(updatedCart, sdk);

    // Dispatch per-item success
    dispatch(addToCartItemSuccess(itemKey, itemsByAuthor, totalItems));
    // Keep global success for backward compatibility
    dispatch(
      addToCartSuccess(itemsByAuthor, totalItems, {
        listingId,
        queryID,
        ...userTokenMaybe,
      })
    );

    // Auto-clear notification after 3 seconds
    setTimeout(() => {
      dispatch(clearNotification());
    }, 3000);

    return updatedCart;
  } catch (err) {
    const storableErr = storableError(err);
    const userFriendlyMessage = getUserFriendlyCartErrorMessage(storableErr, null);
    const errorWithMessage = {
      ...storableErr,
      message: userFriendlyMessage
    };

    // Dispatch per-item error
    dispatch(addToCartItemError(itemKey, errorWithMessage));
    // Keep global error for backward compatibility
    dispatch(addToCartError(storableErr));

    // Auto-clear notification after 5 seconds for errors
    setTimeout(() => {
      dispatch(clearNotification());
    }, 5000);

    // Re-throw error to prevent navigation to cart page
    throw err;
  }
};

export const removeFromCart = itemId => async (dispatch, getState, sdk) => {
  try {
    dispatch(removeFromCartRequest(itemId));
    const { isAuthenticated } = getState().auth;

    // RAS-2508: goes through the shared write queue, same as addToCart/updateCart.
    const updatedCart = await enqueueCartWrite(async () => {
      if (!isAuthenticated) {
        const nextCart = removeItemFromSessionStorageCart(itemId);
        // Ensure React sees the inProgress state change before success
        // (without this, React 18 batches both dispatches into one render)
        await Promise.resolve();
        return nextCart;
      }

      const userRes = await sdk.currentUser.show();
      const currentUser = userRes.data.data;
      const cart = currentUser?.attributes?.profile?.publicData?.cart || [];
      const nextCart = cart.filter(item => item.id !== itemId);

      await sdk.currentUser.updateProfile({
        publicData: {
          cart: nextCart,
        },
      });

      return nextCart;
    });

    const { itemsByAuthor, totalItems } = await getItemsByAuthor(updatedCart, sdk);
    const sortedUpdatedItems = sortItemsByAuthorRecency(itemsByAuthor);
    dispatch(removeFromCartSuccess(sortedUpdatedItems, totalItems));
    return sortedUpdatedItems;
  } catch (error) {
    dispatch(removeFromCartError(storableError(error)));
  }
};

// RAS-2507: applies a single cart item's update (quantity/dates/fees/fulfillment)
// against a raw, flat cart array - the same shape stored on the profile / in
// sessionStorage. Exported for direct unit testing.
//
// A date change that lands on a listing+dates+package another entry already occupies
// merges into that entry (summing quantity, dropping the edited item's own row) rather
// than leaving two rows for the same booking - mirrors addToCart's existing merge
// behavior for the identical collision. Otherwise the item is updated in place by id:
// same id, same position, only the changed fields replaced.
export const applyCartItemUpdate = (rawCart, params) => {
  const {
    id,
    quantity,
    bookingDates,
    usageMultipleValues,
    selectedFeeTypes,
    pickupAtOwnerLocation,
    returnAtOwnerLocation,
  } = params;

  if (!id) {
    throw new Error('cart item id is missing!');
  }

  const targetItem = rawCart.find(i => i.id === id);
  if (!targetItem) {
    throw new Error('Cart item not found');
  }

  const quantityMaybe = quantity || quantity == 0 ? { quantity: Number(quantity) } : {};
  const bookingDatesMaybe =
    bookingDates && bookingDates.startDate && bookingDates.endDate ? { bookingDates } : {};
  const usageMultipleValuesMaybe = usageMultipleValues ? { usageMultipleValues } : {};
  const selectedFeeTypesMaybe = selectedFeeTypes ? { selectedFeeTypes } : {};
  const pickupAtOwnerLocationMaybe = pickupAtOwnerLocation !== undefined ? { pickupAtOwnerLocation } : {};
  const returnAtOwnerLocationMaybe = returnAtOwnerLocation !== undefined ? { returnAtOwnerLocation } : {};

  const isDateChange =
    !!bookingDatesMaybe.bookingDates &&
    (!moment(targetItem.bookingDates?.startDate).isSame(bookingDates.startDate) ||
      !moment(targetItem.bookingDates?.endDate).isSame(bookingDates.endDate));

  const mergeTargetItem = isDateChange
    ? rawCart.find(
        i =>
          i.id !== id &&
          i.listingId === targetItem.listingId &&
          i.package === targetItem.package &&
          moment(i.bookingDates?.startDate).isSame(bookingDates.startDate) &&
          moment(i.bookingDates?.endDate).isSame(bookingDates.endDate)
      )
    : null;

  const editedQuantity = quantityMaybe.quantity ?? Number(targetItem.quantity || 1);
  const finalQuantityForListing = mergeTargetItem
    ? Number(mergeTargetItem.quantity || 1) + editedQuantity
    : editedQuantity;

  const nextCart = rawCart.reduce((acc, item) => {
    if (mergeTargetItem && item.id === mergeTargetItem.id) {
      acc.push({ ...item, quantity: finalQuantityForListing });
      return acc;
    }
    if (item.id === id) {
      if (mergeTargetItem) {
        // Absorbed into mergeTargetItem above - this row is dropped, not kept.
        return acc;
      }
      acc.push({
        ...item,
        ...quantityMaybe,
        ...bookingDatesMaybe,
        ...usageMultipleValuesMaybe,
        ...selectedFeeTypesMaybe,
        ...pickupAtOwnerLocationMaybe,
        ...returnAtOwnerLocationMaybe,
      });
      return acc;
    }
    acc.push(item);
    return acc;
  }, []);

  return {
    nextCart: nextCart.filter(i => Number(i.quantity)),
    listingId: targetItem.listingId,
    isMerge: !!mergeTargetItem,
    finalQuantityForListing,
    // Whether this write actually changes how many units of the listing the edited
    // entry holds - callers use it to skip the stock guard (and its listings.show
    // round trip) for writes that only touch fees/fulfillment, e.g. the Store Cart
    // persisting a forced self-pickup flag on close.
    quantityChanged:
      !!mergeTargetItem || Number(targetItem.quantity || 1) !== editedQuantity,
    // The dates the edited entry ends up on - the stock guard only counts OTHER
    // entries of the same listing on these exact dates.
    resultBookingDates: bookingDatesMaybe.bookingDates || targetItem.bookingDates,
    excludedIds: mergeTargetItem ? [id, mergeTargetItem.id] : [id],
  };
};

// Units of `listingId` already in `rawCart` on the same booking dates as
// `bookingDates`, ignoring `excludedIds`. This is what a cart write's stock guard has
// to compare against: a listing's stock caps how many units ONE booking can take, and
// two bookings of the same listing on different dates are separate bookings (the
// same tent can be rented this weekend and next). addToCart has always counted it
// this way (getExistingCartItem matches on dates); updateCart used to sum the listing
// across every date range in the cart instead, so with a 1-unit listing already
// booked on two other weekends, saving anything about the third booking - even a
// pickup flag persisted when the Store Cart closes - failed with "Maximum quantity
// available is 1. You already have 2 other items in your cart." Overlapping-but-
// different ranges are not checked here by either path; checkout's own
// TRANSACTION_LISTING_INSUFFICIENT_STOCK is the authority for that.
const countListingUnitsOnDates = (rawCart, listingId, bookingDates, excludedIds = []) =>
  rawCart.reduce((sum, item) => {
    if (item.listingId !== listingId || excludedIds.includes(item.id)) return sum;
    if (
      !bookingDates?.startDate ||
      !moment(item.bookingDates?.startDate).isSame(bookingDates.startDate) ||
      !moment(item.bookingDates?.endDate).isSame(bookingDates.endDate)
    ) {
      return sum;
    }
    return sum + Number(item.quantity || 1);
  }, 0);

export const updateCart = params => async (dispatch, getState, sdk) => {
  const { id, authorId, bookingDates } = params;

  // Best-effort listingId for per-item progress tracking, from Redux state - only
  // drives spinner/error keys, not the write itself, so staleness here is harmless.
  // listingId itself never changes for a given cart item id, so this stays accurate
  // even if quantity/dates in Redux are momentarily behind the server.
  const trackedItems = (getState().cart.items || {})[authorId];
  const trackedItem = trackedItems?.find(i => i.id === id);
  const trackedListingId = trackedItem?.listing?.id?.uuid || trackedItem?.listingId;
  const itemKey = trackedListingId ? getItemOperationKey(trackedListingId, bookingDates, id) : null;

  dispatch(updateCartRequest());
  if (trackedListingId && itemKey) {
    dispatch(updateCartItemRequest(itemKey, trackedListingId, id));
  }

  try {
    if (!id) {
      throw new Error('cart item id is missing!');
    }

    const { isAuthenticated } = getState().auth;

    // RAS-2508: read-compute-write goes through the shared write queue, so this
    // always starts from the freshest cart at the moment it actually runs.
    const { nextCart, listingId } = await enqueueCartWrite(async () => {
      const rawCart = isAuthenticated
        ? (await sdk.currentUser.show()).data.data?.attributes?.profile?.publicData?.cart || []
        : getCartFromSession() || [];

      const result = applyCartItemUpdate(rawCart, params);

      // Validate combined quantity against listing stock whenever this write actually
      // changes how much of the listing sits in this booking - a real quantity edit,
      // or a date-edit merge that sums two entries into one. A write that only touches
      // fees/fulfillment (or re-sends the unchanged quantity) skips the guard and its
      // listings.show round trip.
      if (result.quantityChanged && result.listingId) {
        const listingRes = await sdk.listings.show({ id: result.listingId });
        const listing = listingRes?.data?.data;
        const maxAvailableSeats = listing?.attributes?.publicData?.quantity;

        const totalQuantityForListing = countListingUnitsOnDates(
          rawCart,
          result.listingId,
          result.resultBookingDates,
          result.excludedIds
        );
        const totalQuantityAfterUpdate = totalQuantityForListing + result.finalQuantityForListing;

        if (maxAvailableSeats != null && totalQuantityAfterUpdate > maxAvailableSeats) {
          throw new Error(`Maximum quantity available is ${maxAvailableSeats}. You already have ${totalQuantityForListing} other items in your cart.`);
        }
      }

      if (isAuthenticated) {
        await sdk.currentUser.updateProfile({ publicData: { cart: result.nextCart } });
      } else {
        // Ensure React sees the inProgress state change before success
        // (without this, React 18 batches both dispatches into one render)
        await Promise.resolve();
        saveCartToSession(result.nextCart);
      }

      return result;
    });

    // Re-denormalize (listing/author/image data) for the Redux dispatch, same as
    // addToCart's finalization step - the raw array the write queue worked with has
    // no `.listing` attached.
    const { itemsByAuthor, totalItems } = await getItemsByAuthor(nextCart, sdk);
    const sortedItemsByAuthor = sortItemsByAuthorRecency(itemsByAuthor);

    if (listingId && itemKey) {
      dispatch(updateCartItemSuccess(itemKey, sortedItemsByAuthor, totalItems));
    }
    dispatch(updateCartSuccess(sortedItemsByAuthor, totalItems));

    // Auto-clear notification after 3 seconds
    setTimeout(() => {
      dispatch(clearNotification());
    }, 3000);
  } catch (err) {
    const storableErr = storableError(err);
    const userFriendlyMessage = getUserFriendlyCartErrorMessage(storableErr, null);
    const errorWithMessage = {
      ...storableErr,
      message: userFriendlyMessage
    };

    if (trackedListingId && itemKey) {
      dispatch(updateCartItemError(itemKey, errorWithMessage));
    }
    dispatch(updateCartError(storableErr));

    // Auto-clear notification after 5 seconds for errors
    setTimeout(() => {
      dispatch(clearNotification());
    }, 5000);
  }
};

// RAS-2504: cart-drawer Add-ons removal needs to know whether an updateCart write
// actually succeeded, so it can revert its optimistic UI on failure - but updateCart
// itself deliberately never rejects (several existing callers, e.g. quantity +/-,
// dispatch it fire-and-forget with no .catch, and changing that would turn those into
// unhandled rejections). Rather than altering updateCart's own contract, this thin
// wrapper reuses it exactly as-is and reads back the same itemOperationErrors entry it
// already writes (cleared by UPDATE_CART_ITEM_REQUEST, set by UPDATE_CART_ITEM_ERROR)
// once the write has settled. cartItemId is always provided by every caller here, so
// getItemOperationKey's cartItemId branch (`cart-item-${id}`) is the only one that ever
// actually resolves - passing null for the other two args it also accepts keeps this
// aligned with the exact key updateCart computes internally for the same item id.
export const updateCartAndReportResult = params => async (dispatch, getState) => {
  const itemKey = getItemOperationKey(null, null, params.id);
  await dispatch(updateCart(params));
  const error = getState().cart.itemOperationErrors[itemKey];
  return { success: !error, error: error || null };
};

/**
 * RAS-2506 follow-up: applies the same bookingDates to a whole group of cart items
 * (a multi-item Apply from the cart drawer's date-range editor) as ONE atomic
 * read-modify-write, instead of dispatching `updateCart` once per item.
 *
 * `updateCart` dispatched N times for a group of N items still works item's
 * `enqueueCartWrite` queue (RAS-2508) - each write is safe on its own - but they
 * still land in Redux one at a time, N sequential network round trips apart. A
 * consumer reading `state.cart.items` in between two of those commits sees a
 * genuinely mixed state: some items already on the new dates, some still on the old
 * ones. (This is exactly what caused RAS-2506's "cart drawer briefly shows only one
 * item after Apply" bug - ListingPageCarousel's cartDrawerItems groups items by
 * matching bookingDates, and a mixed-state cart no longer has all N items sharing one
 * date range.) Looping `applyCartItemUpdate` against one in-memory `rawCart`
 * accumulator before a single write removes the mixed state entirely - there is no
 * moment where only some items have the new dates.
 */
export const updateCartItemsBatch = (itemIds, bookingDates) => async (dispatch, getState, sdk) => {
  dispatch(updateCartRequest());

  try {
    if (!itemIds?.length) {
      throw new Error('No cart items to update');
    }

    const { isAuthenticated } = getState().auth;

    const { nextCart } = await enqueueCartWrite(async () => {
      let rawCart = isAuthenticated
        ? (await sdk.currentUser.show()).data.data?.attributes?.profile?.publicData?.cart || []
        : getCartFromSession() || [];

      for (const id of itemIds) {
        // RAS-2506 follow-up: the item-id list is captured synchronously when Apply
        // is clicked (CartDatesEditor.js) - if a different browser tab/action
        // removes one of those items from the cart in the split second before this
        // write reaches it, that id no longer needs updating (it's already gone),
        // not a reason to abort the whole batch for every OTHER item too. Any other
        // failure (a genuine bug, a malformed id) still aborts normally.
        let result;
        try {
          result = applyCartItemUpdate(rawCart, { id, bookingDates });
        } catch (itemErr) {
          if (itemErr.message === 'Cart item not found') {
            continue;
          }
          throw itemErr;
        }

        // Same quantity-vs-stock guard as updateCart's single-item path, run after
        // each step against the accumulating cart (not the original snapshot) - a
        // date change only triggers this when it merges two group entries into one
        // (isMerge), which combines their quantities.
        if (result.isMerge && result.listingId) {
          const listingRes = await sdk.listings.show({ id: result.listingId });
          const listing = listingRes?.data?.data;
          const maxAvailableSeats = listing?.attributes?.publicData?.quantity;

          const totalQuantityForListing = countListingUnitsOnDates(
            rawCart,
            result.listingId,
            result.resultBookingDates,
            result.excludedIds
          );
          const totalQuantityAfterUpdate = totalQuantityForListing + result.finalQuantityForListing;

          if (maxAvailableSeats != null && totalQuantityAfterUpdate > maxAvailableSeats) {
            throw new Error(`Maximum quantity available is ${maxAvailableSeats}. You already have ${totalQuantityForListing} other items in your cart.`);
          }
        }

        rawCart = result.nextCart;
      }

      if (isAuthenticated) {
        await sdk.currentUser.updateProfile({ publicData: { cart: rawCart } });
      } else {
        // Ensure React sees the inProgress state change before success
        // (without this, React 18 batches both dispatches into one render)
        await Promise.resolve();
        saveCartToSession(rawCart);
      }

      return { nextCart: rawCart };
    });

    const { itemsByAuthor, totalItems } = await getItemsByAuthor(nextCart, sdk);
    const sortedItemsByAuthor = sortItemsByAuthorRecency(itemsByAuthor);

    dispatch(updateCartSuccess(sortedItemsByAuthor, totalItems));

    setTimeout(() => {
      dispatch(clearNotification());
    }, 3000);
  } catch (err) {
    const storableErr = storableError(err);
    const userFriendlyMessage = getUserFriendlyCartErrorMessage(storableErr, null);

    dispatch(updateCartError(storableErr));

    setTimeout(() => {
      dispatch(clearNotification());
    }, 5000);

    // Unlike updateCart (read back via itemOperationErrors by design, since several
    // callers fire it fire-and-forget), this thunk has exactly one caller
    // (CartDatesEditor's handleApply) that needs to know synchronously whether the
    // Apply succeeded - rejecting lets it use a normal try/catch instead.
    throw new Error(userFriendlyMessage);
  }
};

// When someone checkout with multiple items
// which belongs to a particular author remove
// the items after successful checkout.
export const clearCartItems = itemsIds => async (dispatch, getState, sdk) => {
  try {
    dispatch(clearCartItemRequest());
    const { isAuthenticated } = getState().auth;

    // RAS-2508: goes through the shared write queue, same as the other cart writes.
    const updatedCart = await enqueueCartWrite(async () => {
      const rawCart = isAuthenticated
        ? (await sdk.currentUser.show()).data.data?.attributes?.profile?.publicData?.cart || []
        : getCartFromSession() || [];
      const nextCart = rawCart.filter(item => !itemsIds.includes(item.id));

      if (isAuthenticated) {
        await sdk.currentUser.updateProfile({
          publicData: {
            cart: nextCart,
          },
        });
      } else {
        saveCartToSession(nextCart);
      }

      return nextCart;
    });

    const { itemsByAuthor, totalItems } = await getItemsByAuthor(updatedCart, sdk);
    const sortedUpdatedCartStores = sortItemsByAuthorRecency(itemsByAuthor);
    dispatch(clearCartItemSuccess(sortedUpdatedCartStores, totalItems));
  } catch (err) {
    dispatch(clearCartItemError(storableError(err)));
  }
};

// ================ RAS-2509: cart-drawer date-picker availability ================ //

const cartTimeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => denormalisedResponseEntities(response));
};

// Mirrors StoreProfilePage.duck.js's fetchTimeSlots exactly (same SDK call, same
// per-listing-per-month reducer shape) - relocated here because the cart drawer opens
// from multiple pages and needs its own cache independent of any one page's duck.
export const fetchCartListingTimeSlots = (listingId, start, end, timeZone) => (
  dispatch,
  getState,
  sdk
) => {
  const monthId = monthIdString(start, timeZone);
  dispatch(fetchCartTimeSlotsRequest(listingId, monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = { perPage: 500, page: 1 };

  return dispatch(cartTimeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchCartTimeSlotsSuccess(listingId, monthId, timeSlots));
      return timeSlots;
    })
    .catch(e => {
      dispatch(fetchCartTimeSlotsError(listingId, monthId, storableError(e)));
      throw e;
    });
};

/**
 * RAS-2509: lazy, parallel, cached, unified blocked-dates fetch for a cart-item
 * group. MUST only be dispatched from a user interaction handler (date picker open)
 * - never on drawer mount or listing page load, per the ticket's non-negotiable
 * performance requirement.
 *
 * - PARALLEL: every unique listingId in the group fires via Promise.all, not sequentially.
 * - CACHED: reuses getMonthsToFetch (same helper StoreProfilePage/ListingPage already
 *   use) against this duck's own monthlyTimeSlotsByListing, so a month already fetched
 *   for a listing this drawer session is never re-fetched.
 * - NON-BLOCKING/PROGRESSIVE: `onListingSettled(result)` fires as each listing's fetch
 *   settles, before the group's Promise.all resolves - callers (e.g. the date-picker
 *   hook) use this to fill in blocked dates incrementally instead of gating the
 *   picker's open on the whole group.
 * - FAIL-SAFE: a listing whose fetch fails has its ENTIRE requested range treated as
 *   blocked, rather than risking an unavailable booking.
 *
 * Returns { blockedDates: Set<string>, failedListingIds: string[] } - the group-level
 * union once every listing has settled.
 */
export const fetchCartGroupAvailability = (
  listingIds,
  rangeStart,
  rangeEnd,
  timeZone,
  onListingSettled
) => (dispatch, getState, sdk) => {
  const uniqueListingIds = [...new Set((listingIds || []).filter(Boolean))];
  if (!uniqueListingIds.length || !rangeStart || !rangeEnd || !timeZone) {
    return Promise.resolve({ blockedDates: new Set(), failedListingIds: [] });
  }

  const neededMonths = getMonthsForDateRange(rangeStart, rangeEnd, timeZone);

  const perListingPromises = uniqueListingIds.map(listingId => {
    const existingMonths = getState().cart.monthlyTimeSlotsByListing[listingId] || {};
    const monthsToFetch = getMonthsToFetch(neededMonths, existingMonths);

    const monthFetchPromises = monthsToFetch.map(monthId => {
      const [year, month] = monthId.split('-').map(Number);
      // Reinterpreted through getStartOf's own tz conversion below, so the exact
      // input instant doesn't matter here - only which wall-clock month it lands on.
      const naiveMonthStart = getStartOf(new Date(Date.UTC(year, month - 1, 1)), 'month', timeZone);
      const naiveMonthEnd = getStartOf(naiveMonthStart, 'month', timeZone, 1, 'months');

      // Sharetribe's timeslots API rejects a `start` more than 1 day in the past -
      // for the month containing today, clamp to today rather than the 1st. Mirrors
      // listingAvailabilityHelpers.js's fetchTimeSlotsForListingDateRange, which
      // hits and handles this exact constraint for the single-listing case.
      const todayStart = getStartOf(new Date(), 'day', timeZone);
      const monthStart = isAfterDate(todayStart, naiveMonthStart) ? todayStart : naiveMonthStart;

      // ...and rejects an `end` more than 366 days in the future. The requested
      // window's own rangeEnd (today + dayCountAvailableForBooking) already respects
      // that, but the LAST needed month's calendar-month-end can fall a few days
      // past it once rounded up to a full month - clamp to whichever is earlier.
      const monthEnd = isAfterDate(naiveMonthEnd, rangeEnd) ? rangeEnd : naiveMonthEnd;

      // A month entirely in the past shouldn't be in neededMonths/monthsToFetch to
      // begin with, but never send Sharetribe a request it will just reject.
      if (!isAfterDate(monthEnd, todayStart)) {
        return Promise.resolve([]);
      }

      // getMonthsForDateRange includes every month touched by [rangeStart, rangeEnd)
      // by month-granularity comparison, which includes the month rangeEnd's own
      // instant falls in even when rangeEnd sits exactly on that month's first
      // instant (e.g. rangeEnd = "start of September" makes the range technically
      // touch September). That trailing month's monthStart and monthEnd both clamp
      // to the same rangeEnd boundary above, producing a zero-width range - never
      // send Sharetribe a request whose end isn't strictly after its start, it
      // rejects with a 400 ("End time must be after start time"), which previously
      // failed this listing's ENTIRE Promise.all and fail-safe-blocked its whole
      // requested range, not just this one degenerate month.
      if (!isAfterDate(monthEnd, monthStart)) {
        return Promise.resolve([]);
      }

      return dispatch(fetchCartListingTimeSlots(listingId, monthStart, monthEnd, timeZone));
    });

    return Promise.all(monthFetchPromises)
      .then(() => {
        // Read back from Redux (not just the months just fetched) so months this
        // listing already had cached from an earlier picker-open this session are
        // still included.
        const monthsState = getState().cart.monthlyTimeSlotsByListing[listingId] || {};
        const timeSlots = neededMonths.flatMap(monthId => monthsState[monthId]?.timeSlots || []);
        const blockedDates = computeListingBlockedDates(timeSlots, rangeStart, rangeEnd, timeZone);
        const result = { listingId, blockedDates, failed: false };
        onListingSettled?.(result);
        return result;
      })
      .catch(() => {
        const result = {
          listingId,
          blockedDates: enumerateDateStrings(rangeStart, rangeEnd, timeZone),
          failed: true,
        };
        onListingSettled?.(result);
        return result;
      });
  });

  return Promise.all(perListingPromises).then(results => {
    const blockedDates = unionBlockedDates(results.map(r => r.blockedDates));
    const failedListingIds = results.filter(r => r.failed).map(r => r.listingId);
    return { blockedDates, failedListingIds };
  });
};
